import {
  MENU_ITEMS,
  TWO_COl_MENU_ITEMS,
  MenuItemTypes,
  MENU_ACCESS_KEY_BY_ITEM_KEY,
} from "../constants/menu";
import { STUDENT_MENU_ITEMS } from "../constants/studentPanelMenu";
import {NO_MENU} from '../constants/noMenu'


// ---------------------Start Admin Panel Menu items---------------

// Menu items not listed in MENU_ACCESS_KEY_BY_ITEM_KEY (e.g. commented-out /
// future items) stay visible - only the 10 permission-governed menus are
// hidden until explicitly granted on the user's menuAccess.
const filterMenuItemsByAccess = (
  items: MenuItemTypes[],
  menuAccess?: Record<string, boolean>
): MenuItemTypes[] => {
  return items.filter((item) => {
    const accessKey = MENU_ACCESS_KEY_BY_ITEM_KEY[item.key];
    if (!accessKey) return true;
    return !!menuAccess?.[accessKey];
  });
};

const getMenuItems = (menuAccess?: Record<string, boolean>) => {
  // NOTE - You can fetch from server and return here as well
  return filterMenuItemsByAccess(MENU_ITEMS, menuAccess);
};

// Reverse of MENU_ACCESS_KEY_BY_ITEM_KEY, keyed by each menu item's `url`
// instead of its `key` - lets a route guard ask "does this URL need a
// menuAccess flag, and which one" in O(1) without re-walking MENU_ITEMS on
// every navigation. Only top-level MENU_ITEMS entries carry both a `url`
// and an access key today, so no recursion into `children` is needed.
const ACCESS_KEY_BY_URL: Record<string, string> = MENU_ITEMS.reduce(
  (acc, item) => {
    const accessKey = MENU_ACCESS_KEY_BY_ITEM_KEY[item.key];
    if (item.url && accessKey) acc[item.url] = accessKey;
    return acc;
  },
  {} as Record<string, string>
);

// True for a URL that isn't one of the permission-governed menu pages at all
// (unrestricted, same as filterMenuItemsByAccess leaving unmapped items
// visible) or that the given menuAccess explicitly grants.
const isRouteAccessible = (
  path?: string,
  menuAccess?: Record<string, boolean>
): boolean => {
  if (!path) return true;
  const accessKey = ACCESS_KEY_BY_URL[path];
  if (!accessKey) return true;
  return !!menuAccess?.[accessKey];
};

// The first MENU_ITEMS page a user's menuAccess actually grants, in menu
// order - used to send a region-scoped user (e.g. districtLevel-only) to a
// page they can see instead of a blank/unauthorized one, both right after
// login and if they try to navigate to a page they don't have.
const getDefaultAccessibleRoute = (
  menuAccess?: Record<string, boolean>
): string | null => {
  const allowed = MENU_ITEMS.find((item) => {
    const accessKey = MENU_ACCESS_KEY_BY_ITEM_KEY[item.key];
    return !!(item.url && accessKey && menuAccess?.[accessKey]);
  });
  return allowed?.url || null;
};

const getHorizontalMenuItems = (menuAccess?: Record<string, boolean>) => {
  // Reuse the same, actively-maintained menu tree as the sidebar so the
  // top menu never drifts out of sync with it (HORIZONTAL_MENU_ITEMS is
  // stale template boilerplate and is intentionally not used here).
  return filterMenuItemsByAccess(MENU_ITEMS, menuAccess);
};

const getTwoColumnMenuItems = () => {
  // NOTE - You can fetch from server and return here as well
  return TWO_COl_MENU_ITEMS;
};

const findAllParent = (
  menuItems: MenuItemTypes[],
  menuItem: MenuItemTypes
): string[] => {
  let parents: string[] = [];
  const parent = findMenuItem(menuItems, menuItem["parentKey"]);

  if (parent) {
    parents.push(parent["key"]);
    if (parent["parentKey"])
      parents = [...parents, ...findAllParent(menuItems, parent)];
  }

  return parents;
};

const findMenuItem = (
  menuItems: MenuItemTypes[] | undefined,
  menuItemKey: MenuItemTypes["key"] | undefined
): MenuItemTypes | null => {
  if (menuItems && menuItemKey) {
    for (var i = 0; i < menuItems.length; i++) {
      if (menuItems[i].key === menuItemKey) {
        return menuItems[i];
      }
      var found = findMenuItem(menuItems[i].children, menuItemKey);
      if (found) return found;
    }
  }
  return null;
};


// ---------------------End Admin Panel Menu items---------------



// ---------------------Start Student Panel Menu items---------------

const getStudentMenuItems = () => {
  // NOTE - You can fetch from server and return here as well  
  return STUDENT_MENU_ITEMS;
};

const getNomenu= () => {
  // NOTE - You can fetch from server and return here as well  
  return NO_MENU;
};


// ---------------------End Student Panel Menu items---------------


export {
  getMenuItems,
  getHorizontalMenuItems,
  getTwoColumnMenuItems,
  findAllParent,
  findMenuItem,
  getStudentMenuItems,
  getNomenu,
  isRouteAccessible,
  getDefaultAccessibleRoute,
};
